import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, TrendingUp, Users, Target, Clock, CheckCircle2, XCircle, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [teamStats, setTeamStats] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      const [dashRes, funnelRes, sourcesRes, perfRes, teamRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${API}/analytics/conversion-funnel`, { withCredentials: true }),
        axios.get(`${API}/analytics/source-tracking`, { withCredentials: true }),
        axios.get(`${API}/analytics/performance`, { withCredentials: true }),
        axios.get(`${API}/analytics/team-performance`, { withCredentials: true })
      ]);

      setDashboard(dashRes.data);
      setFunnel(funnelRes.data);
      setSources(sourcesRes.data);
      setPerformance(perfRes.data);
      setTeamStats(teamRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load analytics');
      setLoading(false);
    }
  };

  // Helper function to format hours to minutes or hours+minutes
  const formatHoursToTime = (hours) => {
    if (!hours || hours === 0) return "0 minutes";
    
    const totalMinutes = Math.round(hours * 60);
    
    if (totalMinutes < 60) {
      return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''}`;
    }
    
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    
    if (mins === 0) {
      return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
    }
    
    return `${hrs} hour${hrs !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return <div className="p-6">Loading analytics...</div>;
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-600">{title}</p>
            <p className={`text-3xl font-bold mt-2 text-${color}-600`}>{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          {Icon && <Icon className={`w-12 h-12 text-${color}-500 opacity-20`} />}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Analytics & Reports</h1>
        <p className="text-sm sm:text-base text-slate-600 mt-2">Track performance and gain insights</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="overview" className="flex-1 sm:flex-none text-xs sm:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="funnel" className="flex-1 sm:flex-none text-xs sm:text-sm">Funnel</TabsTrigger>
          <TabsTrigger value="sources" className="flex-1 sm:flex-none text-xs sm:text-sm">Sources</TabsTrigger>
          <TabsTrigger value="performance" className="flex-1 sm:flex-none text-xs sm:text-sm">Performance</TabsTrigger>
          <TabsTrigger value="team" className="flex-1 sm:flex-none text-xs sm:text-sm">Team</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Leads" 
              value={dashboard?.total_leads || 0}
              subtitle={`${dashboard?.recent_leads_30d || 0} new in last 30 days`}
              icon={Users}
              color="blue"
            />
            <StatCard 
              title="Companies" 
              value={dashboard?.total_companies || 0}
              icon={Target}
              color="purple"
            />
            <StatCard 
              title="Contacts" 
              value={dashboard?.total_contacts || 0}
              icon={Activity}
              color="green"
            />
            <StatCard 
              title="Active Tasks" 
              value={dashboard?.total_tasks || 0}
              icon={CheckCircle2}
              color="orange"
            />
          </div>

          {/* Stage Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Distribution</CardTitle>
              <CardDescription>Leads by stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.stage_distribution && Object.entries(dashboard.stage_distribution).map(([stage, count]) => {
                  const percentage = (count / dashboard.total_leads * 100).toFixed(1);
                  return (
                    <div key={stage} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium capitalize">{stage}</span>
                          <span className="text-sm text-slate-600">{count} ({percentage}%)</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conversion Funnel Tab */}
        <TabsContent value="funnel" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard 
              title="Total Leads" 
              value={funnel?.total_leads || 0}
              icon={Users}
            />
            <StatCard 
              title="Deals Won" 
              value={funnel?.won_count || 0}
              icon={CheckCircle2}
              color="green"
            />
            <StatCard 
              title="Win Rate" 
              value={`${funnel?.win_rate || 0}%`}
              subtitle={`${funnel?.lost_count || 0} lost`}
              icon={TrendingUp}
              color="blue"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Conversion Funnel</CardTitle>
              <CardDescription>Lead progression through sales stages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {funnel?.funnel.map((stage, index) => (
                  <div key={stage.stage} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium capitalize">{stage.stage}</span>
                      <div className="flex gap-4 text-sm text-slate-600">
                        <span>{stage.count} leads ({stage.percentage}%)</span>
                        {index > 0 && (
                          <span className="text-blue-600">{stage.conversion_rate}% conversion</span>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-slate-200 rounded h-12 relative overflow-hidden">
                      <div 
                        className={`h-full rounded transition-all ${
                          stage.stage === 'won' ? 'bg-green-600' :
                          stage.stage === 'lost' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}
                        style={{ width: `${stage.percentage}%` }}
                      >
                        <div className="flex items-center justify-center h-full text-white font-semibold">
                          {stage.count > 0 && stage.count}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Sources Tab */}
        <TabsContent value="sources" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Lead Sources Performance</CardTitle>
              <CardDescription>Track which sources bring the best leads</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sources?.sources.map((source) => (
                  <div key={source.source} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg capitalize">{source.source}</h3>
                        <p className="text-sm text-slate-600">{source.total_leads} total leads</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{source.conversion_rate}%</p>
                        <p className="text-xs text-slate-500">conversion rate</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div className="bg-green-50 rounded p-2">
                        <p className="font-semibold text-green-700">{source.won}</p>
                        <p className="text-xs text-green-600">Won</p>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <p className="font-semibold text-red-700">{source.lost}</p>
                        <p className="text-xs text-red-600">Lost</p>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <p className="font-semibold text-blue-700">{source.in_progress}</p>
                        <p className="text-xs text-blue-600">In Progress</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Deals Won" 
              value={performance?.deals_won || 0}
              icon={CheckCircle2}
              color="green"
            />
            <StatCard 
              title="Deals Lost" 
              value={performance?.deals_lost || 0}
              icon={XCircle}
              color="red"
            />
            <StatCard 
              title="Win Rate" 
              value={`${performance?.win_rate || 0}%`}
              icon={TrendingUp}
              color="blue"
            />
            <StatCard 
              title="Pipeline" 
              value={performance?.leads_in_pipeline || 0}
              subtitle="active leads"
              icon={Activity}
              color="purple"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Response Times
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600">Average Response Time</p>
                  <p className="text-3xl font-bold text-blue-600">{formatHoursToTime(performance?.avg_response_time_hours || 0)}</p>
                  <p className="text-sm text-slate-500">Time to first activity</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Average Conversion Time</p>
                  <p className="text-3xl font-bold text-green-600">{performance?.avg_conversion_time_days || 0} days</p>
                  <p className="text-sm text-slate-500">From lead to won</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Task Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600">Task Completion Rate</p>
                  <p className="text-3xl font-bold text-blue-600">{performance?.task_completion_rate || 0}%</p>
                  <p className="text-sm text-slate-500">{performance?.completed_tasks || 0} of {performance?.total_tasks || 0} tasks</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Overdue Tasks</p>
                  <p className="text-3xl font-bold text-red-600">{performance?.overdue_tasks || 0}</p>
                  <p className="text-sm text-slate-500">Need attention</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team Performance Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Team Performance</CardTitle>
              <CardDescription>{teamStats?.team_members || 0} team members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamStats?.team_stats.map((member) => (
                  <div key={member.user_id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{member.user_name}</h3>
                        <p className="text-sm text-slate-600">{member.total_leads} total leads</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{member.win_rate}%</p>
                        <p className="text-xs text-slate-500">win rate</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div className="bg-green-50 rounded p-2">
                        <p className="font-semibold text-green-700">{member.won}</p>
                        <p className="text-xs text-green-600">Won</p>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <p className="font-semibold text-red-700">{member.lost}</p>
                        <p className="text-xs text-red-600">Lost</p>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <p className="font-semibold text-blue-700">{member.in_progress}</p>
                        <p className="text-xs text-blue-600">In Progress</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
